/**
 * 🍳🌿 Interactive Kitchen Phase 2 — Real-World Culinary Session UI
 *
 * Ships Modules 3 (AI Chef Coach text), 4 (Accessible Recipe Filters),
 * 5 (Breathing Pacer + 2–200 min Simmer/Rest Timer).
 *
 * Deferred:
 *   • M1 Camera Scan          — needs MediaPipe playbook
 *   • M2 Mirror-Match Avatar  — horseblind rule (visual style deferred)
 *   • M6 Culinary Overdrive   — needs owner numeric drops
 *
 * Canon:
 *   /app/memory/INTERACTIVE_KITCHEN_PHASE2_MASTER_CANON_FEB2026.md
 *   /app/memory/OMNIMUSCLE_HOLISTIC_HEALING_PLATFORM_CANON_FEB2026.md
 *
 * TONE GATE: No gym-bro. No aggressive-chef. Warm, empathetic,
 * educational, radical patience. Every recipe is prestige — blender
 * smoothie === plated dinner.
 *
 * API (all under /api/interactive-kitchen):
 *   GET  /catalog                       taxonomy of intents + tags
 *   GET  /recipes?intents=&tags=…       filtered list
 *   GET  /pacers                        breathing pacer profiles
 *   POST /coach/greeting                empathetic session-start text
 *   POST /sessions                      start a session
 *   GET  /sessions/:id                  read (elapsed recomputed)
 *   POST /sessions/:id/touch            ping + optional new_state
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CoachAudioButton from "@/components/CoachAudioButton";
import { InteractiveARShell } from "@/components/InteractiveARShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABEL = {
  PROTEIN_ANCHOR: "🥩 Protein",
  COMPLEX_CARB: "🌾 Complex Carb",
  SOFT_FOOD: "🥑 Soft-Food Anchor",
  LEAFY_GREEN: "🥬 Leafy Green",
  ANTI_INFLAMMATORY: "🫚 Anti-Inflammatory",
  HEART_HEALTHY: "❤️ Heart-Healthy",
  GUT_FRIENDLY: "🌿 Gut-Friendly",
};
const TAG_LABEL = {
  SEATED_PREP: "🪑 Seated Prep",
  ONE_HAND_FRIENDLY: "🖐 One-Hand-Friendly",
  SOFT_TEXTURE: "🥣 Soft Texture",
  BLENDER_ONLY: "🌪 Blender-Only",
  NO_HEAT: "❄️ No Heat",
  SHEET_PAN_DROP: "🍞 Sheet-Pan Drop",
  FIFTEEN_MIN_OR_LESS: "⏱ 15-Min-or-Less",
  LOW_VISION_FRIENDLY: "👁 Low-Vision-Friendly",
};

const STATE_LABEL = {
  mise_en_place: "🔪 Mise-en-Place",
  active: "🍳 Cooking",
  simmer_rest: "🕰 Simmer / Rest",
  plating_cooldown: "🍽 Plating",
  complete: "✅ Complete",
};

function fmtElapsed(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function KitchenPhase2Page() {
  const [catalog, setCatalog] = useState(null);
  const [pacers, setPacers] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(true);
  const [baseline, setBaseline] = useState(null);  // 🍳 what the app already knows

  const [selectedIntents, setSelectedIntents] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [greeting, setGreeting] = useState(null);
  const [pickedRecipe, setPickedRecipe] = useState(null);
  const [pacer, setPacer] = useState("knife_rhythm");
  const [plannedMinutes, setPlannedMinutes] = useState(15);
  const [session, setSession] = useState(null);
  const [tickElapsed, setTickElapsed] = useState(0);

  // Load taxonomy + pacers + baseline once
  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          axios.get(`${API}/interactive-kitchen/catalog`),
          axios.get(`${API}/interactive-kitchen/pacers`),
        ]);
        setCatalog(c.data);
        setPacers(p.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load kitchen catalog.");
      }
    })();
  }, []);

  // 🍳 Pull EVERYTHING the app already knows about the user's eating
  useEffect(() => {
    (async () => {
      try {
        const b = await axios.get(`${API}/interactive-kitchen/baseline`, { withCredentials: true });
        setBaseline(b.data);
      } catch {
        // Unauthenticated / no data — quietly stay null; the panel just won't render.
      }
    })();
  }, []);

  // Load recipes on filter change
  useEffect(() => {
    (async () => {
      setLoadingRecipes(true);
      try {
        const params = new URLSearchParams();
        if (selectedIntents.length) params.set("intents", selectedIntents.join(","));
        if (selectedTags.length) params.set("tags", selectedTags.join(","));
        const r = await axios.get(`${API}/interactive-kitchen/recipes?${params.toString()}`);
        setRecipes(r.data?.recipes || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load recipes.");
      } finally {
        setLoadingRecipes(false);
      }
    })();
  }, [selectedIntents, selectedTags]);

  // Load greeting once (deterministic empathetic text)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/interactive-kitchen/coach/greeting`, {
          days_since_last_session: null,
          dietary_flags: [],
          recent_meals: [],
        });
        setGreeting(r.data);
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Elapsed timer tick while a session is active
  useEffect(() => {
    if (!session || session.state === "complete") return;
    const t = setInterval(() => setTickElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [session]);

  const currentElapsed = useMemo(() => {
    if (!session) return 0;
    return (session.elapsed_seconds || 0) + tickElapsed;
  }, [session, tickElapsed]);

  const toggle = (arr, setArr, key) => {
    setArr(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);
  };

  const startSession = async () => {
    if (!pickedRecipe) {
      toast.info("Pick a recipe first — or start a blank session below.");
      return;
    }
    try {
      const r = await axios.post(`${API}/interactive-kitchen/sessions`, {
        recipe_id: pickedRecipe.recipe_id,
        pacer,
        planned_minutes: plannedMinutes,
      });
      setSession(r.data);
      setTickElapsed(0);
      toast.success(`Session started — take your time.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session.");
    }
  };

  const startBlank = async () => {
    try {
      const r = await axios.post(`${API}/interactive-kitchen/sessions`, {
        recipe_id: null,
        pacer,
        planned_minutes: plannedMinutes,
      });
      setSession(r.data);
      setTickElapsed(0);
      toast.success("Free-cook session started.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session.");
    }
  };

  const transition = async (new_state) => {
    if (!session) return;
    try {
      const r = await axios.post(
        `${API}/interactive-kitchen/sessions/${session.session_id}/touch`,
        { new_state }
      );
      setSession(r.data);
      setTickElapsed(0);
      if (new_state === "complete") {
        toast.success("Session complete. Great job today.");
        // Refresh greeting — steady rhythm badge may have just unlocked
        try {
          const g = await axios.post(`${API}/interactive-kitchen/coach/greeting`, {
            days_since_last_session: 0,
            dietary_flags: [],
            recent_meals: [],
          });
          setGreeting(g.data);
        } catch (_) { /* silent */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update session.");
    }
  };

  const pacerProfile = pacers?.pacers?.[pacer];

  return (
    <div className="min-h-screen flex flex-col bg-black text-white" data-testid="kitchen-phase2-page">
      <Header />
      <InteractiveARShell space="kitchen" fallbackGradient="bg-gradient-to-b from-black via-emerald-950/20 to-black">
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 space-y-8">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/50 backdrop-blur-md p-6" data-testid="kitchen-phase2-hero">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">
            Interactive Kitchen · Phase 2 · Holistic Healing
          </div>
          <div className="mt-1 text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            YOUR KITCHEN. <span className="text-emerald-300">YOUR PACE.</span>
          </div>
          <div className="mt-2 text-sm text-neutral-300 max-w-2xl leading-relaxed">
            No shame, no rush, no failure state. Sweet potato, avocado, banana, oats
            — soft-food anchors are first-class here. Blender-only meals count the
            same as plated dinners. Rest as long as you need — the timer holds up to
            200 minutes without a peep.
          </div>
        </section>

        {baseline && <KitchenBaselinePanel baseline={baseline} />}

        {/* AI CHEF COACH GREETING (M3) */}
        {greeting && (
          <section className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-black p-5" data-testid="chef-coach-greeting">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300">
                🧠 AI Chef Coach · Tone-Gated (No Gym-Bro)
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <CoachAudioButton
                  greetingLines={greeting.lines}
                  domain="kitchen"
                  data-testid="coach-audio-btn"
                />
                {greeting.steady_rhythm?.badge_visible && (
                <div
                  data-testid="steady-rhythm-badge"
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-mint-400/50 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 backdrop-blur-sm"
                  title={`${greeting.steady_rhythm.count} sessions in a steady rhythm`}
                >
                  <span className="text-sm">🌱</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-200">
                    Steady Rhythm · {greeting.steady_rhythm.count}
                  </span>
                </div>
              )}
              {greeting.full_circle?.active && (
                <div
                  data-testid="full-circle-badge"
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1 border border-emerald-300/60 bg-gradient-to-r from-emerald-500/30 to-sky-400/30 backdrop-blur-sm"
                  title={`Kitchen ${greeting.full_circle.kitchen_streak} · Gym ${greeting.full_circle.gym_streak} — both sides steady`}
                >
                  <span className="text-sm">🌿</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-100">
                    Full Circle
                  </span>
                </div>
              )}
              </div>
            </div>
            <div className="space-y-2">
              {greeting.lines?.map((line, i) => (
                <div key={i} className="text-sm text-neutral-200 leading-relaxed italic" data-testid={`coach-line-${i}`}>
                  &ldquo;{line}&rdquo;
                </div>
              ))}
              {greeting.full_circle?.active && greeting.full_circle.line && (
                <div className="mt-3 rounded-lg border border-emerald-300/40 bg-gradient-to-r from-emerald-900/30 to-sky-900/30 px-3 py-2" data-testid="full-circle-line">
                  <div className="text-[9px] uppercase tracking-widest text-emerald-300 mb-0.5">🌿 Full Circle</div>
                  <div className="text-sm text-emerald-100 italic leading-relaxed">
                    &ldquo;{greeting.full_circle.line}&rdquo;
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ACTIVE SESSION (M5) OR SESSION LAUNCHER */}
        {session ? (
          <SessionPanel
            session={session}
            pacerProfile={pacerProfile}
            elapsed={currentElapsed}
            onTransition={transition}
            onClose={() => { setSession(null); setTickElapsed(0); }}
          />
        ) : (
          <section className="rounded-xl border border-neutral-800 bg-black/40 p-5 space-y-4" data-testid="session-launcher">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              🌬️ Session Launcher · Breathing Pacer + 2–200 min Timer
            </div>

            {/* Pacer picker */}
            <div>
              <div className="text-xs text-neutral-400 mb-2">Choose a breathing rhythm</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {pacers && Object.entries(pacers.pacers).map(([key, p]) => {
                  const active = pacer === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setPacer(key)}
                      data-testid={`pacer-btn-${key}`}
                      className={`text-left rounded-xl p-3 border transition ${
                        active
                          ? "bg-gradient-to-br from-emerald-700 to-teal-800 border-transparent ring-2 ring-emerald-400/60"
                          : "bg-black/50 border-neutral-800 hover:border-neutral-600"
                      }`}
                    >
                      <div className="text-sm font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
                        {p.label}
                      </div>
                      <div className="text-[10px] text-neutral-300 mt-1 italic leading-snug">
                        {p.cue_in}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Planned minutes slider */}
            <div>
              <div className="flex items-center justify-between text-xs text-neutral-400 mb-1">
                <span>Planned time — you can always go longer</span>
                <span className="text-emerald-300 font-black" data-testid="planned-minutes">
                  {plannedMinutes} min
                </span>
              </div>
              <input
                type="range"
                min={catalog?.min_minutes || 2}
                max={catalog?.max_minutes || 200}
                value={plannedMinutes}
                onChange={(e) => setPlannedMinutes(Number(e.target.value))}
                className="w-full accent-emerald-500"
                data-testid="planned-minutes-slider"
              />
              <div className="text-[10px] text-neutral-500 italic mt-1">
                Range: 2 to 200 minutes. The timer never expires on you — walk away and come back.
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={startSession}
                disabled={!pickedRecipe}
                data-testid="start-recipe-session-btn"
                className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full transition ${
                  pickedRecipe
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-black"
                    : "bg-neutral-900 text-neutral-600 cursor-not-allowed border border-neutral-800"
                }`}
              >
                {pickedRecipe ? `Start · ${pickedRecipe.name}` : "Pick a recipe below"}
              </button>
              <button
                onClick={startBlank}
                data-testid="start-blank-session-btn"
                className="text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full border border-neutral-700 text-neutral-200 hover:border-emerald-500 hover:text-emerald-200 transition"
              >
                Or start a free-cook session
              </button>
            </div>
          </section>
        )}

        {/* ACCESSIBILITY FILTERS (M4) */}
        <section className="rounded-xl border border-neutral-800 bg-black/30 p-5 space-y-4" data-testid="accessibility-filters">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            ♿🌱 Filter By What Works For You Today
          </div>

          <div>
            <div className="text-xs text-neutral-400 mb-2">Functional intent</div>
            <div className="flex flex-wrap gap-1.5">
              {(catalog?.intents || []).map((i) => {
                const active = selectedIntents.includes(i);
                return (
                  <button
                    key={i}
                    onClick={() => toggle(selectedIntents, setSelectedIntents, i)}
                    data-testid={`intent-chip-${i}`}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border transition ${
                      active
                        ? "bg-emerald-600 text-black border-transparent"
                        : "bg-neutral-950 text-neutral-300 border-neutral-800 hover:border-emerald-500"
                    }`}
                  >
                    {INTENT_LABEL[i] || i}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs text-neutral-400 mb-2">Accessibility & convenience</div>
            <div className="flex flex-wrap gap-1.5">
              {(catalog?.tags || []).map((t) => {
                const active = selectedTags.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggle(selectedTags, setSelectedTags, t)}
                    data-testid={`tag-chip-${t}`}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border transition ${
                      active
                        ? "bg-sky-600 text-black border-transparent"
                        : "bg-neutral-950 text-neutral-300 border-neutral-800 hover:border-sky-500"
                    }`}
                  >
                    {TAG_LABEL[t] || t}
                  </button>
                );
              })}
            </div>
          </div>

          {(selectedIntents.length > 0 || selectedTags.length > 0) && (
            <button
              onClick={() => { setSelectedIntents([]); setSelectedTags([]); }}
              data-testid="clear-filters-btn"
              className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white transition"
            >
              Clear all filters
            </button>
          )}
        </section>

        {/* RECIPE GRID (M4) */}
        <section data-testid="recipe-grid-section">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            {loadingRecipes
              ? "Loading recipes…"
              : `${recipes.length} recipe${recipes.length === 1 ? "" : "s"} — every one is prestige`}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recipe-grid">
            {recipes.map((r) => (
              <RecipeCard
                key={r.recipe_id}
                recipe={r}
                active={pickedRecipe?.recipe_id === r.recipe_id}
                onPick={() => setPickedRecipe(r)}
              />
            ))}
            {!loadingRecipes && recipes.length === 0 && (
              <div className="col-span-full text-center py-8 text-neutral-500 italic border border-dashed border-neutral-800 rounded-xl" data-testid="no-recipes-empty-state">
                No recipes match those filters right now — try loosening one, or clear filters. No wrong choice here.
              </div>
            )}
          </div>
        </section>
      </main>
      </InteractiveARShell>
      <Footer />
    </div>
  );
}

/* ─────────────────────────── Recipe Card ─────────────────────────── */
function RecipeCard({ recipe, active, onPick }) {
  return (
    <button
      onClick={onPick}
      data-testid={`recipe-card-${recipe.recipe_id}`}
      className={`text-left rounded-xl p-4 border transition ${
        active
          ? "border-emerald-500 ring-2 ring-emerald-500/40 bg-gradient-to-br from-emerald-950/60 to-black"
          : "border-neutral-800 hover:border-emerald-500/60 bg-black/40"
      }`}
    >
      <div className="text-3xl mb-2">{recipe.hero_emoji || "🍽️"}</div>
      <div className="text-base font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
        {recipe.name}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
        {recipe.total_minutes} min · {recipe.servings} serving{recipe.servings === 1 ? "" : "s"}
      </div>
      <div className="mt-2 text-xs text-neutral-300 leading-snug">{recipe.one_line}</div>
      <div className="mt-3 flex flex-wrap gap-1">
        {(recipe.tags || []).slice(0, 4).map((t) => (
          <span key={t} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-sky-600/40 bg-sky-950/40 text-sky-200">
            {TAG_LABEL[t] || t}
          </span>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-emerald-300/80 italic leading-snug">
        {recipe.why_it_works}
      </div>
    </button>
  );
}

/* ─────────────────────────── Active Session Panel ─────────────────────────── */
function SessionPanel({ session, pacerProfile, elapsed, onTransition, onClose }) {
  const [breathPhase, setBreathPhase] = useState("in");
  useEffect(() => {
    if (!pacerProfile) return;
    const cycle = () => {
      setBreathPhase("in");
      const t1 = setTimeout(() => setBreathPhase("out"), pacerProfile.inhale_ms + (pacerProfile.hold_ms || 0));
      return () => clearTimeout(t1);
    };
    cycle();
    const interval = setInterval(cycle, pacerProfile.inhale_ms + pacerProfile.exhale_ms + (pacerProfile.hold_ms || 0) * 2);
    return () => clearInterval(interval);
  }, [pacerProfile]);

  const stateOrder = ["mise_en_place", "active", "simmer_rest", "plating_cooldown", "complete"];
  const isComplete = session.state === "complete";
  const planned = session.planned_minutes;
  const elapsedMin = Math.floor(elapsed / 60);
  const overtime = elapsedMin > planned;

  return (
    <section className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-black p-6 space-y-5" data-testid="active-session-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">
            Active Session
          </div>
          <div className="text-2xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }} data-testid="session-state-label">
            {STATE_LABEL[session.state] || session.state}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="close-session-panel-btn"
          className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white transition"
        >
          Close
        </button>
      </div>

      {/* Breathing pacer */}
      {pacerProfile && (
        <div className="flex items-center gap-4">
          <div
            className={`w-24 h-24 rounded-full border-2 border-emerald-400/60 flex items-center justify-center transition-transform duration-[3000ms] ease-in-out ${
              breathPhase === "in" ? "scale-110 bg-emerald-500/20" : "scale-90 bg-emerald-500/5"
            }`}
            data-testid="breath-pacer-ring"
          >
            <span className="text-xs font-black uppercase tracking-widest text-emerald-300">
              {breathPhase === "in" ? "Breathe In" : "Breathe Out"}
            </span>
          </div>
          <div className="text-xs text-neutral-300 italic leading-relaxed">
            <div>{breathPhase === "in" ? pacerProfile.cue_in : pacerProfile.cue_out}</div>
            <div className="mt-1 text-[10px] text-neutral-500 uppercase tracking-widest">
              {pacerProfile.label}
            </div>
          </div>
        </div>
      )}

      {/* Timer */}
      <div className="rounded-xl border border-neutral-800 bg-black/50 p-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Elapsed</div>
        <div className="text-4xl font-black text-white tabular-nums" data-testid="session-elapsed">
          {fmtElapsed(elapsed)}
        </div>
        <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">
          Planned: {planned} min · Max: 200 min
        </div>
        {overtime && (
          <div className="mt-2 text-xs text-emerald-300 italic" data-testid="overtime-normalize">
            Overtime is fine — take all the time you need. The app doesn&apos;t expire on you.
          </div>
        )}
      </div>

      {/* State transitions */}
      {!isComplete && (
        <div className="flex flex-wrap gap-2">
          {stateOrder.filter((s) => s !== session.state).map((s) => (
            <button
              key={s}
              onClick={() => onTransition(s)}
              data-testid={`transition-to-${s}`}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500 hover:text-black transition"
            >
              → {STATE_LABEL[s] || s}
            </button>
          ))}
        </div>
      )}

      {isComplete && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-900/20 p-4" data-testid="session-complete-banner">
          <div className="text-sm font-black text-emerald-300">Complete · Great job today.</div>
          <div className="text-xs text-neutral-300 mt-1 italic">
            Every path to the plate counts. Partial prep is a full win.
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────── Kitchen Baseline Panel ─────────────────── */
/* Everything the app already knows about the user's eating.
   Read-only summary: macro targets, TDEE, kitchen bay inventory,
   top foods, top ingredients, AI Coach dietary notes, streaks.    */
function KitchenBaselinePanel({ baseline }) {
  const t = baseline?.targets || {};
  const tdee = baseline?.tdee || {};
  const bay = baseline?.kitchen_bay || [];
  const top = baseline?.top_foods || [];
  const ings = baseline?.top_ingredients || [];
  const sig = baseline?.signals || {};
  const macros = baseline?.macro_averages_7d || {};
  const diet = baseline?.profile_diet || {};
  const ai = baseline?.ai_coach_diet;

  const hasNothing =
    !t.calories && !tdee.tdee && bay.length === 0 && top.length === 0 &&
    ings.length === 0 && !ai && (sig.food_log_total || 0) === 0;
  if (hasNothing) return null;

  return (
    <section
      className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur-md p-5 sm:p-6"
      data-testid="kitchen-baseline-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Your Kitchen · Auto-Pulled</div>
          <div className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            EVERYTHING THE KITCHEN KNOWS
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Macro targets + TDEE */}
        {(t.calories || tdee.tdee) && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">Targets · TDEE</div>
            {tdee.tdee    && <KBRow k="TDEE"        v={`${tdee.tdee} kcal`} />}
            {tdee.bmr     && <KBRow k="BMR"         v={`${tdee.bmr} kcal`} />}
            {t.calories   && <KBRow k="Target kcal" v={`${t.calories}`} />}
            {t.protein_g  && <KBRow k="Protein"     v={`${t.protein_g} g`} />}
            {t.carbs_g    && <KBRow k="Carbs"       v={`${t.carbs_g} g`} />}
            {t.fat_g      && <KBRow k="Fat"         v={`${t.fat_g} g`} />}
          </div>
        )}

        {/* 7-day macro averages */}
        {(macros.kcal > 0 || macros.protein_g > 0) && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">
              Last 7 days · avg / day · logged {sig.days_logged_last_7 || 0}d
            </div>
            <KBRow k="Calories" v={`${macros.kcal || 0} kcal`} />
            <KBRow k="Protein"  v={`${macros.protein_g || 0} g`} />
            <KBRow k="Carbs"    v={`${macros.carbs_g || 0} g`} />
            <KBRow k="Fat"      v={`${macros.fat_g || 0} g`} />
            {macros.fiber_g > 0 && <KBRow k="Fiber" v={`${macros.fiber_g} g`} />}
          </div>
        )}

        {/* Kitchen Bay (cooked meals ready to go) */}
        {bay.length > 0 && (
          <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-4" data-testid="kitchen-bay-inventory">
            <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-2">Kitchen Bay · {bay.length}</div>
            <div className="space-y-1.5">
              {bay.slice(0, 5).map((b) => (
                <div key={b.bay_id} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span>{b.icon || "🍽️"}</span>
                    <span className="text-neutral-200 truncate">{b.item_name}</span>
                  </span>
                  <span className="text-neutral-500 font-mono">{b.heal_hp ? `+${b.heal_hp}hp` : b.rarity}</span>
                </div>
              ))}
              {bay.length > 5 && <div className="text-[10px] text-neutral-500 mt-1">+ {bay.length - 5} more</div>}
            </div>
          </div>
        )}

        {/* Most-eaten foods */}
        {top.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-neutral-950/60 p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Your top foods</div>
            <div className="flex flex-wrap gap-1.5">
              {top.slice(0, 8).map((f) => (
                <span key={f.name} className="text-[11px] px-2 py-1 rounded bg-neutral-900 text-neutral-200 border border-white/10">
                  {f.name} <span className="text-neutral-500">×{f.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Top ingredients (from AI recipes) */}
        {ings.length > 0 && (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/10 p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-2">Ingredients you cook with</div>
            <div className="flex flex-wrap gap-1.5">
              {ings.slice(0, 10).map((i) => (
                <span key={i.name} className="text-[11px] px-2 py-1 rounded bg-emerald-950/40 text-emerald-200 border border-emerald-500/20 capitalize">
                  {i.name} <span className="text-emerald-500/60">×{i.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Coach dietary notes */}
        {ai && (ai.goal || ai.notes) && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">AI Coach · Dietary</div>
            {ai.goal      && <KBRow k="Goal"        v={ai.goal} />}
            {ai.body_type && <KBRow k="Body type"   v={ai.body_type} />}
            {ai.notes     && <div className="mt-2 text-[11px] italic text-amber-200/80 border-t border-amber-500/20 pt-2">&ldquo;{ai.notes}&rdquo;</div>}
          </div>
        )}

        {/* Profile dietary preferences */}
        {(diet.diet_type || diet.allergies || diet.preferred_cuisines) && (
          <div className="rounded-xl border border-violet-500/30 bg-violet-950/20 p-4">
            <div className="text-[10px] uppercase tracking-widest text-violet-300 mb-2">From your profile</div>
            {diet.diet_type          && <KBRow k="Diet"        v={diet.diet_type} />}
            {diet.allergies          && <KBRow k="Allergies"   v={String(diet.allergies)} />}
            {diet.dislikes           && <KBRow k="Dislikes"    v={String(diet.dislikes)} />}
            {diet.preferred_cuisines && <KBRow k="Cuisines"    v={String(diet.preferred_cuisines)} />}
            {diet.cooking_skill      && <KBRow k="Skill"       v={diet.cooking_skill} />}
          </div>
        )}

        {/* Activity signals */}
        <div className="rounded-xl border border-white/10 bg-neutral-950/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Kitchen signals</div>
          <KBRow k="Meals logged"       v={String(sig.food_log_total ?? 0)} />
          <KBRow k="Custom foods"       v={String(sig.custom_foods_saved ?? 0)} />
          <KBRow k="Cook sessions"      v={`${sig.kitchen_sessions_completed ?? 0} / ${sig.kitchen_sessions_total ?? 0}`} />
          <KBRow k="AI recipes"         v={String(sig.ai_recipes_total ?? 0)} />
          <KBRow k="Food streak"        v={`${sig.food_streak_current ?? 0} · best ${sig.food_streak_longest ?? 0}`} />
        </div>
      </div>
    </section>
  );
}

function KBRow({ k, v }) {
  return (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className="text-neutral-400">{k}</span>
      <span className="font-mono text-neutral-200 truncate max-w-[60%] text-right">{v}</span>
    </div>
  );
}
